import React, { useEffect, useState } from 'react';
import { statusOptions } from '../productCatalogTypes';
import { useCategoriesStore } from '../../../../store/categoriesStore';
import { useSkusStore } from '../../../../store/skusStore';
import { ImagePicker } from '../../../../components/ImagePicker';
import styles from './AddProductModal.module.css';

interface AddProductModalProps {
  open: boolean;
  onClose: () => void;
}

export const AddProductModal: React.FC<AddProductModalProps> = ({ open, onClose }) => {
  const [productName, setProductName] = useState('');
  const [brand, setBrand] = useState('');
  const [image, setImage] = useState<string | null>(null);
  const [sku, setSku] = useState('');
  const [upcBarcode, setUpcBarcode] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const categories = useCategoriesStore((s) => s.items);
  const fetchCategories = useCategoriesStore((s) => s.fetchList);
  const createSku = useSkusStore((s) => s.create);
  const refreshSkus = useSkusStore((s) => s.fetchList);

  useEffect(() => {
    if (open && categories.length === 0) fetchCategories().catch(() => undefined);
  }, [open, categories.length, fetchCategories]);

  const [cost, setCost] = useState('');
  const [msrp, setMsrp] = useState('');
  const [mapPrice, setMapPrice] = useState('');
  const [tierA, setTierA] = useState('');
  const [tierB, setTierB] = useState('');
  const [tierC, setTierC] = useState('');
  const [tierD, setTierD] = useState('');

  const [batchTracking, setBatchTracking] = useState(false);
  const [expiryTracking, setExpiryTracking] = useState(false);
  const [shelfLife, setShelfLife] = useState('0');
  const [expiryWarning, setExpiryWarning] = useState('0');
  const [fefoPicking, setFefoPicking] = useState(true);

  const [currentStock, setCurrentStock] = useState('0');
  const [reorderPoint, setReorderPoint] = useState('0');
  const [reorderQty, setReorderQty] = useState('0');
  const [velocityDay, setVelocityDay] = useState('0');

  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.modalTitle}>Add New Product</h2>
            <p className={styles.modalSubtitle}>Fill in the details below &middot; <span className={styles.required}>*</span> = Required</p>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {/* Section 1: Basic Information */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <svg className={styles.sectionIconGreen} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
              <h3 className={styles.sectionTitle}>1. Basic Information</h3>
            </div>
            <div className={styles.divider} />

            <div className={styles.row2}>
              <div className={styles.field}>
                <label className={styles.label}>Product Name <span className={styles.required}>*</span></label>
                <input className={styles.input} placeholder="e.g., Monster Energy Original" value={productName} onChange={(e) => setProductName(e.target.value)} />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Brand <span className={styles.required}>*</span></label>
                <input className={styles.input} placeholder="e.g., Monster" value={brand} onChange={(e) => setBrand(e.target.value)} />
              </div>
            </div>

            <div className={styles.row2}>
              <div className={styles.field}>
                <label className={styles.label}>SKU <span className={styles.required}>*</span></label>
                <input className={styles.input} placeholder="e.g., MON-001-24" value={sku} onChange={(e) => setSku(e.target.value)} />
                <span className={styles.hint}>Internal product code</span>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>UPC Barcode <span className={styles.required}>*</span></label>
                <input className={styles.input} placeholder="e.g., 070847811800" value={upcBarcode} onChange={(e) => setUpcBarcode(e.target.value)} />
                <span className={styles.hint}>12-digit barcode number</span>
              </div>
            </div>

            <div className={styles.row2}>
              <div className={styles.field}>
                <label className={styles.label}>Category <span className={styles.required}>*</span></label>
                <div className={styles.selectWrapper}>
                  <select className={styles.select} value={category} onChange={(e) => setCategory(e.target.value)}>
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={String(cat.id)}>{cat.name}</option>
                    ))}
                  </select>
                  <svg className={styles.selectArrow} viewBox="0 0 24 24" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>
                </div>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Status <span className={styles.required}>*</span></label>
                <div className={styles.selectWrapper}>
                  <select className={styles.select} value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="">Select Status</option>
                    {statusOptions.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <svg className={styles.selectArrow} viewBox="0 0 24 24" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <ImagePicker value={image} onChange={setImage} />
            </div>
          </div>

          {/* Section 2: Cost & Tier Pricing */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <svg className={styles.sectionIconYellow} viewBox="0 0 24 24" fill="currentColor"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/></svg>
              <h3 className={styles.sectionTitle}>2. Cost & Tier Pricing</h3>
            </div>
            <div className={styles.divider} />

            <div className={styles.tipBanner}>
              <svg className={styles.tipIcon} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>
              <span><strong>Pricing Tip:</strong> Set your cost first, then tier prices. Margins will calculate automatically.</span>
            </div>

            <div className={styles.row3}>
              <div className={styles.field}>
                <label className={styles.label}>Your Cost (per case) <span className={styles.required}>*</span></label>
                <div className={styles.currencyInput}>
                  <span className={styles.currencySymbol}>$</span>
                  <input className={styles.input} style={{ paddingLeft: 28 }} placeholder="0.00" value={cost} onChange={(e) => setCost(e.target.value)} />
                </div>
                <span className={styles.hint}>What you pay supplier</span>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>MSRP (Optional)</label>
                <div className={styles.currencyInput}>
                  <span className={styles.currencySymbol}>$</span>
                  <input className={styles.input} style={{ paddingLeft: 28 }} placeholder="0.00" value={msrp} onChange={(e) => setMsrp(e.target.value)} />
                </div>
                <span className={styles.hint}>Suggested retail price</span>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>MAP Price (Optional)</label>
                <div className={styles.currencyInput}>
                  <span className={styles.currencySymbol}>$</span>
                  <input className={styles.input} style={{ paddingLeft: 28 }} placeholder="0.00" value={mapPrice} onChange={(e) => setMapPrice(e.target.value)} />
                </div>
                <span className={styles.hint}>Minimum advertised</span>
              </div>
            </div>

            <div className={styles.tierBox}>
              <p className={styles.tierLabel}>Set wholesale prices for each customer tier:</p>
              <div className={styles.tierGrid}>
                <div className={styles.tierCard} style={{ borderColor: '#059669' }}>
                  <label className={styles.tierCardLabel} style={{ color: '#059669' }}>Tier A (Best Customers)</label>
                  <div className={styles.currencyInput}>
                    <span className={styles.currencySymbol}>$</span>
                    <input className={styles.input} style={{ paddingLeft: 28 }} placeholder="0.00" value={tierA} onChange={(e) => setTierA(e.target.value)} />
                  </div>
                </div>
                <div className={styles.tierCard} style={{ borderColor: '#2563EB' }}>
                  <label className={styles.tierCardLabel} style={{ color: '#2563EB' }}>Tier B (Good Customers)</label>
                  <div className={styles.currencyInput}>
                    <span className={styles.currencySymbol}>$</span>
                    <input className={styles.input} style={{ paddingLeft: 28 }} placeholder="0.02" value={tierB} onChange={(e) => setTierB(e.target.value)} />
                  </div>
                </div>
                <div className={styles.tierCard} style={{ borderColor: '#D97706' }}>
                  <label className={styles.tierCardLabel} style={{ color: '#D97706' }}>Tier C (Okay Customers)</label>
                  <div className={styles.currencyInput}>
                    <span className={styles.currencySymbol}>$</span>
                    <input className={styles.input} style={{ paddingLeft: 28 }} placeholder="0.00" value={tierC} onChange={(e) => setTierC(e.target.value)} />
                  </div>
                </div>
                <div className={styles.tierCard} style={{ borderColor: '#DC2626' }}>
                  <label className={styles.tierCardLabel} style={{ color: '#DC2626' }}>Tier D (Low Volume)</label>
                  <div className={styles.currencyInput}>
                    <span className={styles.currencySymbol}>$</span>
                    <input className={styles.input} style={{ paddingLeft: 28 }} placeholder="0.00" value={tierD} onChange={(e) => setTierD(e.target.value)} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Expiry and Batch Tracking */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <svg className={styles.sectionIconGreen} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
              <h3 className={styles.sectionTitle}>3. Expiry and Batch Tracking</h3>
            </div>
            <div className={styles.divider} />

            <div className={styles.toggleRow}>
              <button
                className={`${styles.toggle} ${batchTracking ? styles.toggleOn : ''}`}
                onClick={() => setBatchTracking(!batchTracking)}
              >
                <span className={styles.toggleKnob} />
              </button>
              <span className={styles.toggleLabel}>Requires Batch Tracking</span>
            </div>

            <div className={styles.toggleRow}>
              <button
                className={`${styles.toggle} ${expiryTracking ? styles.toggleOn : ''}`}
                onClick={() => setExpiryTracking(!expiryTracking)}
              >
                <span className={styles.toggleKnob} />
              </button>
              <span className={styles.toggleLabel}>Requires Expiry Tracking</span>
            </div>

            <div className={styles.row2}>
              <div className={styles.field}>
                <label className={styles.label}>Shelf Life(Days)</label>
                <input className={styles.input} placeholder="0" value={shelfLife} onChange={(e) => setShelfLife(e.target.value)} />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Expiry Warning(Days)</label>
                <input className={styles.input} placeholder="0" value={expiryWarning} onChange={(e) => setExpiryWarning(e.target.value)} />
              </div>
            </div>

            <div className={styles.toggleRow}>
              <button
                className={`${styles.toggle} ${fefoPicking ? styles.toggleOn : ''}`}
                onClick={() => setFefoPicking(!fefoPicking)}
              >
                <span className={styles.toggleKnob} />
              </button>
              <span className={styles.toggleLabel}>FEFO Picking</span>
            </div>
          </div>

          {/* Section 4: Inventory */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <svg className={styles.sectionIconGreen} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
              <h3 className={styles.sectionTitle}>4. Inventory (Optional - Can Add Later)</h3>
            </div>
            <div className={styles.divider} />

            <div className={styles.row4}>
              <div className={styles.field}>
                <label className={styles.label}>Current Stock</label>
                <input className={styles.input} placeholder="0" value={currentStock} onChange={(e) => setCurrentStock(e.target.value)} />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Reorder Point</label>
                <input className={styles.input} placeholder="0" value={reorderPoint} onChange={(e) => setReorderPoint(e.target.value)} />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Reorder Qty</label>
                <input className={styles.input} placeholder="0" value={reorderQty} onChange={(e) => setReorderQty(e.target.value)} />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Velocity/Day</label>
                <input className={styles.input} placeholder="0" value={velocityDay} onChange={(e) => setVelocityDay(e.target.value)} />
              </div>
            </div>
          </div>

          {/* Section 5: Compliance & Tracking */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <svg className={styles.sectionIconGreen} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
              <h3 className={styles.sectionTitle}>5. Compliance & Tracking</h3>
            </div>
            <div className={styles.divider} />

            <div className={styles.complianceList}>
              <div className={styles.complianceItem}>
                <span className={styles.complianceEmoji}>🔞</span>
                <div>
                  <p className={styles.complianceTitle}>Age Verification Required (21+)</p>
                  <p className={styles.complianceSub}>Check if product requires age verification</p>
                </div>
              </div>
              <div className={styles.complianceItem}>
                <span className={styles.complianceEmoji}>📦</span>
                <div>
                  <p className={styles.complianceTitle}>Lot/Batch Tracking</p>
                  <p className={styles.complianceSub}>Track by batch numbers for recalls</p>
                </div>
              </div>
              <div className={styles.complianceItem}>
                <span className={styles.complianceEmoji}>📊</span>
                <div>
                  <p className={styles.complianceTitle}>MSA Reporting</p>
                  <p className={styles.complianceSub}>Include in compliance reports</p>
                </div>
              </div>
            </div>

            <p className={styles.requiredNote}><span className={styles.required}>*</span> = Required field</p>
          </div>
        </div>

        {submitError && (
          <div style={{ margin: '12px 24px', padding: 12, background: '#FEF2F2', color: '#B91C1C', borderRadius: 6 }}>
            {submitError}
          </div>
        )}

        {/* Footer */}
        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            className={styles.submitBtn}
            disabled={submitting}
            onClick={async () => {
              setSubmitError(null);
              if (!sku.trim() || !productName.trim() || !cost) {
                setSubmitError('SKU, product name, and cost are required');
                return;
              }
              setSubmitting(true);
              try {
                await createSku({
                  sku_code: sku.trim(),
                  barcode: upcBarcode.trim() || null,
                  product_name: productName.trim(),
                  description: brand ? `Brand: ${brand}` : undefined,
                  category_id: category ? Number(category) : null,
                  unit_cost: parseFloat(cost) || 0,
                  list_price: tierA ? parseFloat(tierA) : null,
                  status: status === 'Active' ? 'active' : status === 'Inactive' ? 'inactive' : status === 'Discontinued' ? 'discontinued' : 'active',
                  image_url: image,
                  reorder_point: parseInt(reorderPoint, 10) || 0,
                  reorder_qty: parseInt(reorderQty, 10) || 0,
                });
                await refreshSkus();
                onClose();
              } catch (err) {
                setSubmitError(err instanceof Error ? err.message : 'Failed to add product');
              } finally {
                setSubmitting(false);
              }
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
            {submitting ? 'Adding…' : 'Add Product to Catalog'}
          </button>
        </div>
      </div>
    </div>
  );
};
